import { AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount, minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { colocaToast } from "@/shared/utils/toast";

import { useArmDelay } from "../../hooks/useArmDelay";
import { useBatchErrorMessage } from "../../hooks/useBatchErrorMessage";
import { useRetryFailedItems } from "../../hooks/useRetryFailedItems";
import type { PayoutBatchDetail, RetryPreviewItem } from "../../types/payoutBatches.types";

export interface RetryFailedItemsDialogProps {
  batch: PayoutBatchDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applyBatch: (next: PayoutBatchDetail) => void;
  onRetried: () => void;
}

/**
 * The two-step retry: the failed rows are priced again and shown with their new debit, and only that total is
 * approved (it travels as `max_total_debit`). If the server re-prices higher on approval, the preview reloads
 * and the person reads it again.
 */
export function RetryFailedItemsDialog({
  batch,
  open,
  onOpenChange,
  applyBatch,
  onRetried,
}: RetryFailedItemsDialogProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const describeError = useBatchErrorMessage();
  const retry = useRetryFailedItems(batch.id, applyBatch);
  const { armed, disarm } = useArmDelay(open);
  const [reviewed, setReviewed] = useState(false);
  const source = batch.source_currency;

  const { loadPreview } = retry;
  useEffect(() => {
    if (open) void loadPreview();
  }, [open, loadPreview]);

  const close = () => {
    setReviewed(false);
    disarm();
    retry.reset();
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && retry.isRetrying) return;
    if (!next) close();
    else onOpenChange(true);
  };

  const approve = async () => {
    const accepted = await retry.retry();
    if (accepted) {
      colocaToast.success(t("retry_success_toast"));
      onRetried();
      close();
    }
  };

  const columns = useMemo<ColocaDataTableColumn<RetryPreviewItem>[]>(
    () => [
      {
        id: "row",
        header: t("retry_col_row"),
        headerClassName: "w-16",
        cell: (row) => <span className="font-mono text-xs tabular-nums">{row.row_number}</span>,
      },
      {
        id: "debit",
        header: t("retry_col_new_debit"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (row) =>
          row.pricing ? (
            <ColocaAmountDisplay
              value={row.pricing.total_debit_source}
              currency={source}
              fractionDigits={minorUnitsFor(source)}
              size="sm"
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "issue",
        header: t("retry_col_issue"),
        cell: (row) =>
          row.error ? (
            <span className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              {row.error.message}
            </span>
          ) : (
            <CheckCircle2 aria-hidden="true" className="size-4 text-success" />
          ),
      },
    ],
    [source, t],
  );

  const preview = retry.preview;
  const retryable = preview?.retryable_count ?? 0;
  const canApprove =
    !!preview && retryable > 0 && reviewed && armed && !retry.isRetrying && !retry.isPreviewing;

  return (
    <>
      <ColocaDialog
        open={open}
        onOpenChange={handleOpenChange}
        size="lg"
        title={t("retry_title")}
        description={t("retry_description")}
        showCloseButton={!retry.isRetrying}
        footer={
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={retry.isRetrying} />}>
              {t("action_cancel")}
            </ColocaDialogClose>
            <ColocaButton
              variant="primary"
              icon={RotateCcw}
              loading={retry.isRetrying}
              disabled={!canApprove}
              onClick={() => void approve()}
            >
              {armed ? t("retry_confirm") : t("retry_arming")}
            </ColocaButton>
          </>
        }
      >
        <div className="space-y-4">
          {retry.debitChanged && (
            <ColocaAlert variant="warning" icon={AlertTriangle} text={t("retry_debit_changed")} />
          )}
          {retry.error && (
            <ColocaAlert variant="destructive" icon={AlertTriangle} text={describeError(retry.error)} />
          )}

          {retry.previewError ? (
            <ColocaAlert
              variant="destructive"
              icon={AlertTriangle}
              text={describeError(retry.previewError)}
              actions={
                <ColocaButton variant="default" size="xs" onClick={() => void retry.loadPreview()}>
                  {t("error_retry")}
                </ColocaButton>
              }
            />
          ) : (
            <ColocaDataTable
              columns={columns}
              rows={preview?.items ?? []}
              rowKey={(row) => row.id}
              loading={retry.isPreviewing}
              skeletonRows={4}
              empty={<p className="p-4 text-sm text-muted-foreground">{t("retry_none_retryable")}</p>}
              footer={
                preview ? (
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <span className="text-muted-foreground">
                      {t("retry_retryable_count", { count: retryable })}
                    </span>
                    <span className="font-mono font-semibold text-foreground tabular-nums">
                      {t("retry_total", {
                        amount: `${formatCurrencyAmount(preview.total_debit, source, activeLanguage)} ${source}`,
                      })}
                    </span>
                  </div>
                ) : undefined
              }
            />
          )}

          <ColocaCheckbox
            checked={reviewed}
            onCheckedChange={setReviewed}
            disabled={retry.isRetrying || retryable === 0}
            label={t("retry_reviewed_label")}
          />
        </div>
      </ColocaDialog>
      <TwoFactorActionModals
        controller={retry.twoFactor}
        requiredDescription={t("retry_two_factor_required")}
      />
    </>
  );
}
